import React, { useEffect, useState } from 'react'
import TextField from '@mui/material/TextField';
import ProfileCombo from '../../sharedComponents/ProfileCombo';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomerProfiles, selectCustomerProfiles } from '../../redux/features/customerProfileSlice';
import { toast } from 'react-toastify';
import useManageOrder from '../order/useManageOrder';
import OrderService from '../order/OrderService';
import useCloudinary from '../../hooks/useCloudinary';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Modal from 'react-modal';
import ReceiptNotMatchModal from './ReceiptNotMatchModal';
import checkReceipt from '../../utils/checkReceipt';
import readQrData from '../../utils/readQrData';
import ReceiptPasteModal from './ReceiptPasteModal';
import useCustomerProfile from '../customerProfile/useCustomerProfile';
import checkBlacklistResultAfterPaid from '../../utils/checkBlacklistResultAfterPaid';
import { extractUrls, compareUrlsWithOrders } from '../../services/UrlValidationService';

/** Standard URL+receipt product payment does not apply customer advance — block and direct staff to advance or ship-date flow */
const STANDARD_PAY_BLOCKED_PENDING_ADVANCE =
  'ลูกค้ามียอดตัดค้าง (มียอดฝากไว้) ไม่สามารถใช้การชำระสินค้า กรุณาใช้ปุ่ม "จ่ายค่าส่ง" เพื่อแนบสลิปแทน';

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)', // This sets the overlay color
    zIndex: 100
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: '500px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

// Wider variant for the advance-payment preview (long urls need room).
const advancePreviewStyles = {
  overlay: customStyles.overlay,
  content: {
    ...customStyles.content,
    width: '760px',
    maxWidth: '92vw',
    maxHeight: '85vh',
  },
};

const ProductPaid = ({openCustomPayment, setShipDatePaymentSmileName, refreshPendingOrder}) => {

  const dispatch = useDispatch()
  const profiles = useSelector(selectCustomerProfiles)
  const shopName = useSelector((state) => state.shop.name);

  const [files, setFiles] = useState(null);
  const [urls, setUrls] = useState([])
  const [smileName, setSmileName] = useState('')
  const [isLoading, setIsLoading] = useState(false);
  const [receipValueState, setReceiptValueState] = useState('')
  const [isCreditPaid, setIsCreditPaid] = useState(false)
  const [isAdvancePayment, setIsAdvancePayment] = useState(false)
  const [advanceAmount, setAdvanceAmount] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPastingModalOpen, setIsPastingModalOpen] = useState(false);
  const [issueFormData, setIssueFormData] = useState('')
  const [textFieldValue, setTextFieldValue] = useState('');
  const [profileState, setProfileState] = useState({})
  const [isAdvancePreviewOpen, setIsAdvancePreviewOpen] = useState(false)
  const [advancePreview, setAdvancePreview] = useState(null)
  const [pendingAdvanceForm, setPendingAdvanceForm] = useState(null)

  const profileHasPendingAdvance = () => {
    const n = Number(profileState?.advanceAmount);
    return Number.isFinite(n) && n > 0;
  };

  const {
    getAmountFromUrl, 
    updateOrderAndUploadReceipt, 
    error: orderError
  } = useManageOrder()

  const {uploadReceiptImage , error} = useCloudinary()
  const {checkAndRemoveBlacklistIfNeeded} = useCustomerProfile();

  const handleCheckboxChange = () => {
    setIsCreditPaid(!isCreditPaid);
  };

  const handleAdvancePaymentChange = () => {
    setIsAdvancePayment(!isAdvancePayment);
    if (!isAdvancePayment) {
      setAdvanceAmount(''); // Clear amount when unchecking
    }
  };

  const clearDataAfterSuccess = () => {
    setIsCreditPaid(false)
    setIsAdvancePayment(false)
    setAdvanceAmount('')
    setFiles(null)
    setTextFieldValue('')
    refreshPendingOrder()
  }

  // Advance-payment (ตัดยอด) preview → confirm. Before actually settling a lump sum,
  // preview which urls it will pay + the leftover, so the admin can review and inform
  // the customer BEFORE the payment is committed (afterwards it's easy to forget).
  const fmtBaht = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
  const buildAdvanceText = (pv) => {
    if (!pv) return ''
    let t = `รับชำระ ${fmtBaht(pv.newAmount)} บาท`
    if (Number(pv.existingAdvance) > 0) t += ` (รวมยอดฝากไว้เดิม ${fmtBaht(pv.existingAdvance)} บาท)`
    if ((pv.allocations || []).length === 0) {
      t += ` — ไม่มีรายการที่ตัดยอดได้ เก็บเป็นยอดฝากไว้ทั้งหมด\n`
    } else {
      t += ` — ตัดยอดให้รายการดังนี้:\n`
      ;(pv.allocations || []).forEach((a) => { t += `• ${a.url} — ${fmtBaht(a.amountApplied)} บาท ✅\n` })
    }
    t += `มียอดฝากไว้คงเหลือ: ${fmtBaht(pv.leftover)} บาท`
    return t
  }
  const previewThenConfirm = async (formData, amount) => {
    try {
      const res = await OrderService.previewAdvancePayment({ smileName, shop: shopName, amount })
      setAdvancePreview(res.data)
      setPendingAdvanceForm(formData)
      setIsAdvancePreviewOpen(true)
    } catch (e) {
      const msg = e?.response?.data?.message || 'ไม่สามารถดึงข้อมูลการตัดยอดได้'
      toast.error(msg)
      alert(`⚠️ ข้อผิดพลาด: ${msg}`)
    } finally {
      setIsLoading(false)
    }
  }
  const confirmAdvanceSubmit = async () => {
    if (!pendingAdvanceForm) return
    setIsLoading(true)
    // try/finally: an unexpected throw must never leave isLoading stuck true — the
    // preview modal blocks closing while loading, so a stuck flag froze the whole screen.
    try {
      const response = await updateOrderAndUploadReceipt(pendingAdvanceForm)
      setIsAdvancePreviewOpen(false)
      if (response == true) {
        await checkBlacklistResultAfterPaid(profileState, checkAndRemoveBlacklistIfNeeded)
        toast.success("Update Successful!!")
        clearDataAfterSuccess()
      } else {
        const errorMessage = response || 'เกิดข้อผิดพลาดในการอัพเดทข้อมูล'
        toast.error(errorMessage)
        alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง`)
      }
    } finally {
      setIsLoading(false)
      setPendingAdvanceForm(null)
    }
  }

  function appendOrderData(formData,orders){
    orders.forEach(order => {
      formData.append("orderReceipts", JSON.stringify({id:order.id, amountType: order.isDown ? 'productPaymentFirstHalf' : 'productPayment'}))
      if(order.deliveryFeeJp !== null && !order.isCod){
        formData.append("orderReceipts", JSON.stringify({id:order.id, amountType: order.isDown ? 'shipJpFirstHalf' : 'shipJpPaid'}))
      }
    })
    return formData
  }

  const handleConfirmPastedImage = (file) => {
    setFiles(file);
    const fileCount = Array.isArray(file) ? file.length : (file ? 1 : 0);
    OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'receipt_paste_modal_confirmed_with_file', smileName, shop: shopName, detail: { fileCount } });
  };

  const handleOpenCustomPaymentClick = () => {
    if(smileName){
      setShipDatePaymentSmileName(smileName)
      openCustomPayment()
    }else{
      toast.error("กรุณาเลือก ชื่อลูกค้าก่อน")
    }
  }
  const handleGetAmount = async () => {
    OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_button_pressed', smileName, shop: shopName, detail: { hasFiles: !!(files && (Array.isArray(files) ? files.length : true)), urlsCount: urls.length } });
    if(smileName){
      // The name must be a real profile of the CURRENT shop. Profile Search also
      // fires on plain typing, so a name that was typed but never picked from the
      // list (or a profile that belongs to the other shop) reached the API and came
      // back 500 "Customer not found" — which the UI showed as a raw JS error.
      // profileState is the same exact-smileName lookup used by the pending-advance
      // guard below, so that guard is only meaningful once this one has passed.
      if(!profileState){
        OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'profile_not_in_shop_list' } });
        const errorMessage = `ไม่พบโปรไฟล์ "${smileName}" ในร้านนี้ กรุณาเลือกชื่อจากรายการ Profile Search`;
        toast.error(errorMessage);
        alert(`⚠️ ${errorMessage}`);
        return;
      }

      // Validate advance payment - no URLs allowed
      if(isAdvancePayment && urls.length > 0){
        OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'urls_not_allowed_when_advance_payment' } });
        toast.error("ไม่สามารถใส่ลิ้งค์สินค้าเมื่อเลือกตัดยอด")
        return;
      }
      
      // Validate non-advance payment - URLs are required
      if(!isAdvancePayment && urls.length === 0){
        OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'no_urls' } });
        toast.error("กรุณาใส่ลิ้งค์สินค้า")
        return;
      }
      
      // Validate advance payment - amount is required only when both credit card and advance payment are checked
      if(isAdvancePayment && isCreditPaid && (!advanceAmount || advanceAmount <= 0)){
        OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'advance_amount_required' } });
        toast.error("กรุณากรอกยอดที่ตัด")
        return;
      }

      if (!isAdvancePayment && profileHasPendingAdvance()) {
        OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'standard_payment_blocked_pending_advance' } });
        toast.error(STANDARD_PAY_BLOCKED_PENDING_ADVANCE);
        return;
      }

      const formData = {
        urls: urls,
        smileName : smileName
      }      
      if(files){
        try{
          setIsLoading(true)
          const response = await getAmountFromUrl(formData)
          const { totalSum , orders, unmatched } = response
          const hasCompletePayment = orders.some(order => order.productPayment !== 'รอชำระ'); //check that all return calculation is รอชำระ or not
          if(hasCompletePayment){
            OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'some_orders_already_paid' } });
            const errorMessage = "มีบางรายการที่ลูกค้าชำระแล้ว";
            toast.error(errorMessage);
            alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบรายการสินค้า`);
            setIsLoading(false)
          }else if(!compareUrlsWithOrders(urls, orders)){
            // Diagnostics go to the event log + console ONLY (admin still sees the
            // generic message): which pasted urls matched no order and why (backend
            // `unmatched`: not_found vs wrong_status), plus duplicate pastes (same
            // link twice = double order — this flow checks 1 link : 1 order).
            const urlCounts = urls.reduce((m, u) => { m[u] = (m[u] || 0) + 1; return m; }, {});
            const duplicateUrls = Object.entries(urlCounts).filter(([, n]) => n > 1).map(([u, n]) => ({ url: u, count: n }));
            console.log('[ProductPaid] รายการไม่ตรงกัน diagnostics:', { unmatched: unmatched || [], duplicateUrls });
            OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'urls_not_match_orders', unmatched: unmatched || [], duplicateUrls } });
            const errorMessage = "รายการไม่ตรงกัน";
            toast.error(errorMessage);
            alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบลิ้งค์สินค้าที่กรอก`);
            setIsLoading(false)
          }else{
            const formData2 = new FormData();
            formData2.append('urls', JSON.stringify(urls));
            formData2.append('smileName', smileName);
            files.forEach((file) => {
              formData2.append('images', file);
            });
            const orderAddedFormData = appendOrderData(formData2, orders) // append for update DB (order ID and amount type)
            
            if(isCreditPaid && isAdvancePayment){ //Case Both Credit Card and Advance Payment
              orderAddedFormData.append('accountNo', 'Shipnity');
              orderAddedFormData.append('amount', advanceAmount); // Use advanceAmount instead of totalSum
              orderAddedFormData.append('paymentDateTime', new Date());
              orderAddedFormData.append('receiptIssue', true);
              orderAddedFormData.append('isCreditPaid', true);
              orderAddedFormData.append('isAdvancePayment', true);
              orderAddedFormData.append('advanceAmount', advanceAmount);
              orderAddedFormData.append('frontendSource', 'ProductPaid');
              OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'previewAdvancePayment', smileName, shop: shopName, detail: 'both credit and advance payment' });
              await previewThenConfirm(orderAddedFormData, advanceAmount)
            } else if(isCreditPaid){ //Case Credit Card Only
              orderAddedFormData.append('accountNo', 'Shipnity');
              orderAddedFormData.append('amount', totalSum);
              orderAddedFormData.append('paymentDateTime', new Date());
              orderAddedFormData.append('receiptIssue', true);
              orderAddedFormData.append('isCreditPaid', true);
              orderAddedFormData.append('frontendSource', 'ProductPaid');
              OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'updateOrderAndUploadReceipt', smileName, shop: shopName, detail: 'credit paid only' });
              const response = await updateOrderAndUploadReceipt(orderAddedFormData)
              if(response == true){
                await checkBlacklistResultAfterPaid(profileState, checkAndRemoveBlacklistIfNeeded)
                toast.success("Update Successful!!")
                clearDataAfterSuccess()
              }else{
                const errorMessage = response || 'เกิดข้อผิดพลาดในการอัพเดทข้อมูล';
                toast.error(errorMessage);
                alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง`);
              }
              setIsLoading(false)
            } else if(isAdvancePayment){ //Case Advance Payment Only
              const receiptData = await checkReceiptValue(orderAddedFormData)
              if(!receiptData.errorStatus){
                const receiptValue = receiptData.totalSum
                const combinedFormData = receiptData.formData
                setReceiptValueState(receiptValue)
                if (receiptValue) {
                  // Don't append amount again - it's already in combinedFormData from checkReceiptValue
                  combinedFormData.append('isAdvancePayment', true);
                  combinedFormData.append('advanceAmount', receiptValue);
                  combinedFormData.append('frontendSource', 'ProductPaid');
                  OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'previewAdvancePayment', smileName, shop: shopName, detail: 'advance payment with receipt' });
                  await previewThenConfirm(combinedFormData, receiptValue)
                } else {
                  const errorMessage = "ไม่สามารถอ่านยอดจากสลิปได้";
                  toast.error(errorMessage);
                  alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบสลิปและลองใหม่อีกครั้ง`);
                  setIsLoading(false)
                }
              }else{
                setIsLoading(false)
              }
            } else {
              const receiptData = await checkReceiptValue(orderAddedFormData)
              if(!receiptData.errorStatus){
                const receiptValue = receiptData.totalSum
                const combinedFormData = receiptData.formData
                orderAddedFormData.append('totalAmount', totalSum);
                setReceiptValueState(receiptValue)
                if (receiptValue && Math.abs(receiptValue - totalSum) < 10) {
                  combinedFormData.append('frontendSource', 'ProductPaid');
                  OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'updateOrderAndUploadReceipt', smileName, shop: shopName, detail: 'receipt match' });
                  const response = await updateOrderAndUploadReceipt(combinedFormData)
                  setIsLoading(false)
                  if(response === true){
                    await checkBlacklistResultAfterPaid(profileState, checkAndRemoveBlacklistIfNeeded)
                    refreshPendingOrder()
                    toast.success("Update Successful!!")
                    clearDataAfterSuccess()
                  }else{
                    const errorMessage = response || 'เกิดข้อผิดพลาดในการอัพเดทข้อมูล';
                    toast.error(errorMessage);
                    alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง`);
                  }
                } else {
                  OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_amount_not_match', smileName, shop: shopName, detail: { receiptValue: receiptValue, totalSum, diff: Math.abs(receiptValue - totalSum) } });
                  setIsModalOpen(true)
                  setIssueFormData(combinedFormData)
                  //toast.error("ยอดรวมรายการไม่ตรงกับ สลิป")
                  setIsLoading(false)
                }
              }else{
                setIsLoading(false)
              }
            }
          }
        }catch(err){
          setIsLoading(false);
          const errorMessage = err?.response?.data?.message || err?.message || 'เกิดข้อผิดพลาดที่ไม่คาดคิด';
          OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'unexpected_error', smileName, shop: shopName, detail: { error: errorMessage } });
          toast.error(errorMessage);
          alert(`⚠️ เกิดข้อผิดพลาด: ${errorMessage}\n\nกรุณาลองใหม่อีกครั้ง และตรวจสอบว่าข้อมูลถูกบันทึกหรือไม่`);
        }
      }else{
        OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'no_receipt_attached' } });
        toast.error("กรุณาแนบสลิป");
        alert('⚠️ กรุณาแนบสลิปก่อนกดบันทึก');
      }
    }else{
      OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'no_profile_selected' } });
      toast.error("Please select Profile first.");
      alert('⚠️ กรุณาเลือกโปรไฟล์ลูกค้าก่อน');
    }
  }

  const handleIssueSave = async (note) => {
    if (profileHasPendingAdvance()) {
      OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'issue_save_blocked_pending_advance' } });
      toast.error(STANDARD_PAY_BLOCKED_PENDING_ADVANCE);
      setIsModalOpen(false);
      return;
    }
    setIsLoading(true)
    issueFormData.append('receiptIssue',true)
    issueFormData.append('note',note)
    issueFormData.append('frontendSource', 'ProductPaid');
    OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'updateOrderAndUploadReceipt', smileName, shop: shopName, detail: 'issue save' });
    const response = await updateOrderAndUploadReceipt(issueFormData)

    setIsLoading(false)
    if(response === true){
      toast.success("Update Successful!!")
      setIssueFormData('')
      clearDataAfterSuccess()
      setIsModalOpen(false)
      await checkBlacklistResultAfterPaid(profileState, checkAndRemoveBlacklistIfNeeded)
    }else{
      const errorMessage = response || 'เกิดข้อผิดพลาดในการอัพเดทข้อมูล';
      toast.error(errorMessage);
      alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง`);
    }
  }

  const checkReceiptValue = async (formData) => {
    try {
      let errorStatus = false;
      let totalSum = 0;
      // Read all QR codes in parallel
      const qrCodePromises = files.map(file => readQrData(file).catch(error => {
        const errorMessage = error || 'ไม่สามารถอ่าน QR Code จากสลิปได้';
        toast.error(errorMessage);
        alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบสลิปและลองใหม่อีกครั้ง`);
        errorStatus = true
        return null; // Return null or an appropriate value if QR code read fails
      }));
      const qrCodes = await Promise.all(qrCodePromises);
  
      // Prepare receipt check promises
      const receiptPromises = qrCodes.map((qrCodeData, index) => {
        if (qrCodeData) {
          return checkReceipt(qrCodeData).then(receiptData => {
            if (receiptData) {
              formData.append('accountNo', receiptData.data.receiver.account.value);
              formData.append('amount', receiptData.data.amount);
              formData.append('paymentDateTime', receiptData.data.transTimestamp);
              totalSum += parseFloat(receiptData.data.amount);
            } else {
              // checkReceipt resolved but returned no data — treat as read failure
              const errorMessage = 'ไม่สามารถอ่านข้อมูลจากสลิปได้ กรุณาตรวจสอบสลิปอีกครั้ง';
              toast.error(errorMessage);
              alert(`⚠️ ข้อผิดพลาด: ${errorMessage}`);
              errorStatus = true;
            }
          }).catch(error => {
            const errorMessage = error || 'ไม่สามารถตรวจสอบสลิปได้';
            toast.error(errorMessage);
            alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบสลิปและลองใหม่อีกครั้ง`);
            errorStatus = true;
          });
        }
      });
  
      // Wait for all receipt checks to complete
      await Promise.all(receiptPromises);
      return { totalSum, formData, errorStatus };
    } catch (error) {
      setIsLoading(false);
      console.error("Error processing receipts:", error);
      const errorMessage = error?.message || 'เกิดข้อผิดพลาดในการประมวลผลสลิป';
      toast.error(errorMessage);
      alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาลองใหม่อีกครั้ง`);
      return { totalSum: 0, formData, errorStatus: true };
    }
  }
  
  const handleTextChange = (e) => {
    setTextFieldValue(e.target.value);
    const urls = extractUrls(e.target.value);
    setUrls(urls);
  }

  useEffect(() => {
    dispatch(getCustomerProfiles(shopName))
  }, [])

  const closeModal = () => {
    OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'receipt_not_match_modal_closed_without_save', smileName, shop: shopName, detail: { note: 'admin left without completing Update' } });
    setIsModalOpen(false)
  }

  const handlePastingOpenModal = () => {
    setIsPastingModalOpen(true);
    OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'receipt_paste_modal_opened', smileName, shop: shopName, detail: {} });
  };

  const handlePastingCloseModal = () => {
    const hadFile = !!(files && (Array.isArray(files) ? files.length : true));
    OrderService.logFrontendEvent({ source: 'ProductPaid', action: 'receipt_paste_modal_closed', smileName, shop: shopName, detail: { hadFile } });
    setIsPastingModalOpen(false);
  };

  useEffect(() => {
    const findProfile = () => {
      const profile = profiles.find(profile => profile.smileName === smileName);
      if (profile) {
        setProfileState(profile);
      } else {
        setProfileState(null);
      }
    };
    findProfile();
  }, [smileName, profiles]);

  return (
    <div>
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={customStyles}
        ariaHideApp={false}
      >
        <ReceiptNotMatchModal
          handleSave={handleIssueSave}
        />
      </Modal>
      <Modal
        isOpen={isAdvancePreviewOpen}
        onRequestClose={() => !isLoading && setIsAdvancePreviewOpen(false)}
        style={advancePreviewStyles}
        ariaHideApp={false}
      >
        <div>
          <div className='text-lg font-bold mb-3'>ยืนยันการตัดยอด — ตรวจสอบก่อนบันทึก</div>
          {advancePreview && (
            <>
              <textarea
                readOnly
                value={buildAdvanceText(advancePreview)}
                rows={Math.min(20, (advancePreview.allocations?.length || 0) + 4)}
                className='w-full p-2 border border-gray-300 rounded-md text-sm'
              />
              {advancePreview.allocations?.length === 0 && (
                <div className='text-amber-700 text-sm mt-1'>ℹ️ ยอดนี้ไม่ตัดรายการใด จะเก็บเป็นยอดฝากไว้ทั้งหมด</div>
              )}
              <div className='flex justify-end gap-2 mt-3'>
                <button
                  className='btn-primary'
                  onClick={() => { navigator.clipboard.writeText(buildAdvanceText(advancePreview)); toast.success('คัดลอกแล้ว'); }}
                >
                  Copy
                </button>
                <button
                  className='bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded-md'
                  onClick={() => setIsAdvancePreviewOpen(false)}
                  disabled={isLoading}
                >
                  ยกเลิก
                </button>
                <button
                  className='bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed'
                  onClick={confirmAdvanceSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? 'กำลังบันทึก...' : 'ยืนยันบันทึก'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
      <div className='grid grid-cols-2'>
        <div className='col-span-1'>
          <ProfileCombo 
            columnSpan={6} 
            profiles={profiles} 
            onChange={(smileName) =>{setSmileName(smileName)}} 
          />
        </div>
        <button className='btn-success ml-auto h-10 mt-auto' onClick={handleOpenCustomPaymentClick}>จ่ายค่าส่ง</button>
      </div>
      <TextField
        id="outlined-multiline-flexible"
        label="ลิ้งค์สินค้า ที่ต้องการเก็บเงิน"
        multiline
        maxRows={20}
        fullWidth
        margin="normal"
        value={textFieldValue}
        onChange={handleTextChange}
      />
      {profileHasPendingAdvance() && !isAdvancePayment && (
        <div className="mb-2 p-3 rounded-md bg-amber-50 text-amber-950 text-sm border border-amber-200">
          {STANDARD_PAY_BLOCKED_PENDING_ADVANCE}
        </div>
      )}
      <div className='flex justify-between'>
        <div>
          <div className='col-span-1'>
          <button className='btn-primary' onClick={handlePastingOpenModal}>Paste Receipt</button>
            {/* <label htmlFor='file-upload' className='btn-primary py-3'>Upload Receipt</label>
            <input className="hidden" id="file-upload" type="file" accept="image/*" multiple onChange={handleFileChange} value="" />
            {files && <span className='ml-3'>{files.length} file is selected</span>} */}
            <div className='mt-3'>
              {files && files.length > 0 ? (
                files.map((file, index) => (
                  <div key={index}>{file.name}</div>
                ))
              ) : (
                <div>No files chosen</div>
              )}
            </div>
          </div>
        </div>
        <div>
          <div className='flex'>
            <button className='btn-primary ml-auto' disabled={isLoading} onClick={handleGetAmount}>Save Payment</button>
          </div>
          <div className='flex flex-col space-y-2'>
            <FormControlLabel 
              control={
              <Checkbox 
                checked={isCreditPaid}
                onChange={handleCheckboxChange} 
                />} 
              label="ลูกค้าจ่ายบัตรเครดิต" 
            />
            <FormControlLabel 
              control={
              <Checkbox 
                checked={isAdvancePayment}
                onChange={handleAdvancePaymentChange}
                />} 
              label="ลูกค้าตัดยอด" 
            />
            {isAdvancePayment && isCreditPaid && (
              <TextField
                id="advance-amount"
                label="ยอดที่ตัด"
                type="number"
                value={advanceAmount}
                onChange={(e) => setAdvanceAmount(e.target.value)}
                size="small"
                style={{ width: '200px' }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            )}
          </div>
        </div>
      </div>
    <ReceiptPasteModal isOpen={isPastingModalOpen} onRequestClose={handlePastingCloseModal} onConfirm={handleConfirmPastedImage}  />
      {isLoading && <CircularProgress />} {/* Render spinner when loading */}
      {/* {receipValueState && <span>{receipValueState}</span>} */}
    </div>
  )
}

export default ProductPaid